import { gql, useQuery } from '@apollo/client';
import { Button } from '@apollo/space-kit/Button';
import { colors } from '@apollo/space-kit/colors';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';

import { useShowToast } from 'src/components/toast/Toast';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { proposalChecksRouteConfig, proposalRouteConfig } from '../routes';

export const useRecentPublishChecksToast = ({
  canQueryLaunches,
}: {
  canQueryLaunches: boolean | undefined;
}) => {
  const { graphId, graphVariant } = useRouteParams(proposalRouteConfig);
  const isOnChecksPage = !!useRouteMatch(proposalChecksRouteConfig.definition);
  const { data } = useQuery<
    GraphQLTypes.UseRecentPublishChecksToastQuery,
    GraphQLTypes.UseRecentPublishChecksToastQueryVariables
  >(
    gql`
      query UseRecentPublishChecksToastQuery(
        $graphId: ID!
        $checkWorkflowLimit: Int
        $checkWorkflowFilter: CheckFilterInput
      ) {
        me {
          id
        }
        service(id: $graphId) {
          id
          checkWorkflows(
            filter: $checkWorkflowFilter
            limit: $checkWorkflowLimit
          ) {
            id
            status
            proposalRevision {
              id
              createdBy {
                id
              }
            }
          }
        }
      }
    `,
    {
      pollInterval: 30_000,
      variables: {
        graphId,
        checkWorkflowLimit: 1,
        checkWorkflowFilter: {
          includeProposalChecks: true,
          variants: [graphVariant],
        },
      },
      skip: !canQueryLaunches,
    },
  );

  const [lastHandledRevisionId, setLastHandledRevisionId] = useState<
    string | undefined
  >(undefined);

  const [currentToastId, setCurrentToastId] = useState<string>();
  const { showToasts, hideToasts } = useShowToast();
  const location = useLocation();
  const userId = data?.me?.id;

  // NOTE: Hide toast on location change
  React.useEffect(() => {
    if (currentToastId) return () => hideToasts(currentToastId);
  }, [currentToastId, hideToasts, location.pathname]);

  // Hide toast after 30 seconds always
  React.useEffect(() => {
    if (currentToastId) {
      const timeout = window.setTimeout(
        () => hideToasts(currentToastId),
        30 * 1000,
      );
      return () => window.clearTimeout(timeout);
    }
  }, [hideToasts, currentToastId]);

  const showToast = useCallback(
    (failed: boolean) => {
      if (!isOnChecksPage) {
        const toastIds = showToasts({
          theme: 'dark',
          level: failed ? 'error' : 'success',
          heading: failed
            ? 'Your revision was created successfully, but some of your checks have failed'
            : 'Revision created successfully and all checks have passed!',
          message: failed
            ? 'Head over to the Checks page for more details.'
            : 'To dive into your revision, head over to the Changes page.',
          actions: (closeModal) => {
            return (
              <Button
                // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                // TODO(Maya) add link to synthetics test
                data-testid="proposals_toast_go_to_checks_button"
                theme="dark"
                as={
                  <Link
                    to={proposalChecksRouteConfig.locationFrom({
                      location,
                      fromRouteConfig: proposalRouteConfig,
                    })}
                  />
                }
                onClick={closeModal}
                color={colors.midnight.dark}
              >
                Go to Checks
              </Button>
            );
          },
        });
        setCurrentToastId(toastIds?.[0]);
      }
    },
    [isOnChecksPage, location, showToasts],
  );

  useEffect(() => {
    const checkWorkflow = data?.service?.checkWorkflows[0];
    const revision = checkWorkflow?.proposalRevision;
    if (!lastHandledRevisionId && revision?.id) {
      setLastHandledRevisionId(revision.id);
    } else if (
      revision?.createdBy?.id === userId &&
      revision?.id &&
      revision.id !== lastHandledRevisionId
    ) {
      if (checkWorkflow?.status === GraphQLTypes.CheckWorkflowStatus.FAILED) {
        showToast(true);
        setLastHandledRevisionId(revision.id);
      } else if (
        checkWorkflow?.status === GraphQLTypes.CheckWorkflowStatus.PASSED
      ) {
        showToast(false);
        setLastHandledRevisionId(revision.id);
      }
    }
  }, [data, lastHandledRevisionId, showToast, userId]);
};
